// ProjectIntern.jsx
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import ProjectTabs from "../../components/project_tabs";
import Intel from "./intel";
import ProjectTabAdministration from "../../components/projects_t_administration";
import ProjectTabEngineering from "../../components/projects_t_engineering";
import ProjectTabComercial from "../../components/projects_t_comercial";
import ProjectTabLegal from "../../components/projects_t_legal";
import Sidebar from "../../components/sidebar";
import { Container, SideContainer } from "../main_display/styles";
import { ProjectPageScroll, ProjectBootMessage } from "./styles";
import { useEffect, useMemo, useState } from "react";
import { setDocumentModels } from "../../store/modules/model/actions";
import { blockchainService } from "../../services/blockchainService";
import { getVisibleProjectTabLabels } from "../../utils/userRoles";
import { projectService } from "../../services/projectService";
import { setSelectedProject } from "../../store/modules/projects/actions";
import {
  mergeProjectWithHub,
  normalizeProjectHubPayload,
} from "../../utils/projectHub";

function pickInitialProject(routeProjectId, locationProject, selectedProject) {
  if (!routeProjectId) return null;
  if (
    locationProject?.id != null &&
    String(locationProject.id) === String(routeProjectId)
  ) {
    return locationProject;
  }
  if (
    selectedProject?.id != null &&
    String(selectedProject.id) === String(routeProjectId)
  ) {
    return selectedProject;
  }
  return null;
}

const ProjectIntern = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);
  const user = useSelector((state) => state.user.currentUser);
  const collapsed = useSelector((state) => state.sidebar);
  const navigate = useNavigate();
  const selectedProject = useSelector((state) => state.projects.selectedProject);
  const location = useLocation();
  const { projectId: projectIdParam } = useParams();

  const routeProjectId =
    projectIdParam ||
    location.state?.project?.id ||
    selectedProject?.id ||
    null;

  const [project, setProject] = useState(() =>
    pickInitialProject(
      routeProjectId,
      location.state?.project,
      selectedProject
    )
  );
  const [hub, setHub] = useState(null);
  const [hubLoading, setHubLoading] = useState(false);
  const [hubError, setHubError] = useState(null);
  const [bootLoading, setBootLoading] = useState(Boolean(routeProjectId));
  const [bootError, setBootError] = useState(null);

  useEffect(() => {
    const fetchDocumentModels = async () => {
      try {
        const token = sessionStorage.getItem("Authorization");
        const headers = { Authorization: token };
        const response = await blockchainService.getDocumentModels(headers);
        dispatch(setDocumentModels(response));
      } catch (error) {
        console.error("Erro ao carregar modelos:", error);
      }
    };

    fetchDocumentModels();
  }, [dispatch]);

  /**
   * Uma chamada consolidada: GET /projects/{id}/?scan_limit=25
   * (detecções FIRMS no mapa usam os N scans mais recentes por área)
   * Fallback: GET /projects/{id}/hub/ se o detalhe falhar.
   */
  useEffect(() => {
    if (!routeProjectId) {
      setBootLoading(false);
      setBootError("Projeto não informado.");
      return;
    }

    if (!projectIdParam) {
      navigate(`/intern_project/${routeProjectId}`, {
        replace: true,
        state: location.state,
      });
      return;
    }

    let cancelled = false;

    const applyHub = (normalized, seedProject) => {
      setHub(normalized);
      const merged = mergeProjectWithHub(seedProject || normalized.project, normalized);
      setProject(merged);
      dispatch(setSelectedProject(merged));
    };

    const load = async () => {
      setBootError(null);
      setHubError(null);
      setBootLoading(true);
      setHubLoading(true);

      const seed = pickInitialProject(
        routeProjectId,
        location.state?.project,
        selectedProject
      );
      if (seed) setProject(seed);

      try {
        // Preferir detalhe consolidado (cadastrais no root + blocos)
        const detail = await projectService.getProjectById(routeProjectId, {
          scan_limit: 25,
        });
        if (cancelled) return;

        const raw = detail?.data || detail;
        const normalized = normalizeProjectHubPayload(raw);
        if (!normalized?.project?.id && !normalized?.project?.title) {
          throw new Error("Resposta de projeto inválida");
        }

        applyHub(normalized, seed);
        setBootLoading(false);
        setHubLoading(false);
      } catch (detailError) {
        console.warn(
          "Detalhe consolidado falhou; tentando /hub/…",
          detailError
        );
        if (cancelled) return;

        try {
          const hubRaw = await projectService.getProjectHub(routeProjectId, {
            scan_limit: 25,
          });
          if (cancelled) return;

          const normalized = normalizeProjectHubPayload(hubRaw);
          if (!normalized) throw hubRaw;

          applyHub(normalized, seed);
          setBootLoading(false);
          setHubLoading(false);
        } catch (hubFail) {
          console.error("Erro ao carregar projeto/hub:", hubFail);
          if (cancelled) return;

          const status =
            detailError?.response?.status || hubFail?.response?.status;
          if (!seed) {
            setBootError(
              status === 404
                ? "Projeto não encontrado ou sem acesso."
                : "Não foi possível carregar o projeto."
            );
            setHub(null);
            setHubError(hubFail);
          } else {
            setHub(null);
            setHubError(hubFail);
          }
          setBootLoading(false);
          setHubLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeProjectId, projectIdParam, dispatch, navigate]);

  const handleRegister = () => {
    navigate("/edit_intern_project", {
      state: { project },
    });
  };

  const handleProjectUpdated = (updated) => {
    if (!updated) return;
    setProject((prev) => {
      const next = { ...(prev || {}), ...updated };
      dispatch(setSelectedProject(next));
      return next;
    });
  };

  const allTabs = useMemo(
    () => [
      {
        label: "Informações",
        content: (
          <Intel
            user={user}
            project={project}
            hub={hub}
            hubLoading={hubLoading}
            hubError={hubError}
          />
        ),
      },
      {
        label: "Administrativo",
        content: <ProjectTabAdministration user={user} project={project} />,
      },
      {
        label: "Ambiental",
        content: <ProjectTabEngineering user={user} project={project} />,
      },
      {
        label: "Comercial",
        content: <ProjectTabComercial user={user} project={project} />,
      },
      {
        label: "Jurídico",
        content: <ProjectTabLegal user={user} project={project} />,
      },
    ],
    [user, project, hub, hubLoading, hubError]
  );

  const visibleLabels = useMemo(
    () => getVisibleProjectTabLabels(currentUser),
    [currentUser]
  );

  const tabs = useMemo(
    () => allTabs.filter((tab) => visibleLabels.includes(tab.label)),
    [allTabs, visibleLabels]
  );

  if (!currentUser) {
    return null;
  }

  return (
    <Container collapsed={collapsed}>
      <Sidebar />
      <SideContainer>
        <ProjectPageScroll>
          {bootLoading && !project ? (
            <ProjectBootMessage>Carregando projeto…</ProjectBootMessage>
          ) : bootError && !project ? (
            <ProjectBootMessage>
              <p>{bootError}</p>
              <button type="button" onClick={() => navigate("/projects")}>
                Voltar aos projetos
              </button>
            </ProjectBootMessage>
          ) : project ? (
            <ProjectTabs
              tabs={tabs}
              handleRegister={handleRegister}
              project={project}
              onProjectUpdated={handleProjectUpdated}
            />
          ) : (
            <ProjectBootMessage>
              <p>Projeto não encontrado.</p>
              <button type="button" onClick={() => navigate("/projects")}>
                Voltar aos projetos
              </button>
            </ProjectBootMessage>
          )}
        </ProjectPageScroll>
      </SideContainer>
    </Container>
  );
};

export default ProjectIntern;
